import { TaskFile } from "API/Endpoints/Tasks/payloads";

import { Methodology } from "./Methodology";
import { SubmissionGroups } from "./SubmissionGroups";

export enum SubmissionSlotStatusEnum {
  DATA_ISSUE = "Data issue",
  EMISSIONS_READY = "Emissions ready",
  PROCESSING = "Processing",
  AWAITING_DATA = "Awaiting data",
  PARTIALLY_COMPLETE = "Partially complete",
  NOT_APPLICABLE = "Not applicable",
  CALCULATING = "Calculating",
  COLLECTION_FAILED = "Collection failed",
  FAILED_VALIDATION = "Validation failed",
  UPCOMING = "Upcoming",
}

export enum SubmissionSlotExpectedFormatEnum {
  FILE = "file",
  SURVEY = "survey",
  TEMPLATE = "template",
  INTEGRATION = "integration",
  MULTI_ACTIVITY_FILE = "multi_activity_file",
}

export enum ActionType {
  UPLOAD = "upload",
  ESTIMATE = "estimate",
  LOG = "log",
}

export type SubmissionSlotStatus = `${SubmissionSlotStatusEnum}`;
export type SubmissionSlotExpectedFormat =
  `${SubmissionSlotExpectedFormatEnum}`;

type SubmittedItem = {
  files: number;
  surveys: number;
};

export type SubmissionSlots = {
  id: string;
  dataTaskId: string | null;
  timePeriod: TimePeriod;
  submittedBy: string;
  status: SubmissionSlotStatus;
  lastUpload: string;
  lastUpdate: string;
  submittersName: string;
  submittedItems: SubmittedItem;
  submittersEmail: string;
  hasDraft: boolean;
};

export type ErrorLogFile = {
  name: string;
  extension: string;
  fileId: string;
};

export type SubmissionSlotDataFile = {
  id: string;
  name: string;
  errorLog?: null | ErrorLogFile;
  fileId?: string;
  extension?: string;
  hasVariance?: boolean;
};

export type SubmissionSlotDataItem = {
  id: string;
  name: string;
  files: SubmissionSlotDataFile[];
  expectedFormat: SubmissionSlotExpectedFormat;
  survey?: {
    data: any;
    isValid: boolean;
  };
  submissionTemplate?: {
    name: string;
    lastUpdated: string;
    updatedSinceLatestGroupSubmission?: boolean;
  };
};

export type SubmissionSlotData = SubmissionSlotDataItem[];

export type TimePeriod = {
  label: string;
  id?: string;
  order?: number;
  cadence?: {
    id: string;
    name: string;
  };
};

export type SubmittedFile = {
  activityId: string;
  file: {
    errorLog?: null | ErrorLogFile;
    name: string;
    extension: string;
    humanReadableSize: string;
    fileId: string;
  };
};

export type MethodologyMappings = {
  activity: {
    id: string;
    name: string;
  };
  expectedDataFormat: SubmissionSlotExpectedFormat;
  surveyDefinition?: any;
};

export type ActivitiesTaskFile = {
  expectedDataFormat: SubmissionSlotExpectedFormat;
  id: string;
  name: string;
  methodology: Methodology;
  submittedData: {
    files: TaskFile[];
    surveyData: any;
  };
  surveyDefinition: any;
  isApplicable?: boolean;
  submissionInstructions: string;
  submissionTemplate?: {
    lastUpdated: string;
    name: string;
    updatedSinceLatestGroupSubmission?: boolean;
  };
};

export type SubmissionSlotDetails = {
  dataTaskId: string;
  dataTaskState: string;
  dataTaskPreviousState: string;
  hasDraft: boolean;
  methodologyMappings?: MethodologyMappings[];
  activities?: ActivitiesTaskFile[];
  submissionGroup: SubmissionGroups;
  timePeriod: TimePeriod;
  files?: SubmittedFile[];
  status?: {
    value: string;
    label: SubmissionSlotStatus;
  };
  id: string;
  isReopenable?: boolean;
  isSubmittable?: boolean;
};

export type SubmissionSlotListByOrg = {
  activities: { id: string; name: string }[];
  entities: string[];
  id: string;
  lastDraft: {
    email: string | null;
    name: string | null;
    numberOfFiles: number;
    numberOfSurveys: number;
    uploadedAt: string;
  } | null;
  lastSubmission: {
    email: string | null;
    name: string | null;
    numberOfFiles: number;
    numberOfSurveys: number;
    uploadedAt: string;
  } | null;
  reportingYear: { id: string; label: string };
  status: SubmissionSlotStatus;
  submissionGroup: {
    id: string;
    name: string;
  };
  timePeriod: { id: string; label: string };
};

export type SubmissionSlotGroupedByOrg = {
  activities: { id: string; name: string }[];
  entities: string[];
  id: string;
  lastDrafts: {
    submitters: { name: string | null; email: string | null }[] | null;
    uploadedAt: string;
    numberOfFiles: number;
    numberOfSurveys: number;
  };
  lastSubmissions: {
    submitters: { name: string | null; email: string | null }[] | null;
    uploadedAt: string;
    numberOfFiles: number;
    numberOfSurveys: number;
  };
  name: string;
  reportingYear: { id: string; label: string };
  slotsCount: number;
  statusBreakdown: {
    "Validation failed"?: number;
    "Partially complete"?: number;
    Processing?: number;
    Calculating?: number;
    "Collection failed"?: number;
    "Awaiting data"?: number;
    "Emissions ready"?: number;
    "Not applicable"?: number;
    "Data issue"?: number;
    Upcoming?: number;
  };
};

export enum DataTaskState {
  DRAFT = "draft",
}

export type SubmissionSlotStatusByOrg =
  | "data_issue"
  | "waiting_for_data"
  | "draft";
